import React from 'react';

import { IndexGridNew } from '@rockerbox/styleguide';
import moment from 'moment';
import { Table } from 'semantic-ui-react';

import { options, containerStyles, bannerStyles, bannerHeader, filterContainer } from './constants';
import { PercentCell, DescriptionCell } from './parts';
import { track } from '../../../../utils/tracking';
import { postPixelAnomalyNotificationPreference, putPixelAnomalyNotificationPreference } from '../../../api/appServices';
import { StyledDropdown } from '../../../components';
import { TreeTableLoader } from '../../../components/loaders';
import { updateAnomalyItemStatus } from '../helpers';

const ResolutionStatusCell = handleStatusChange => ({ item }) => {
  const effectiveStatus = item?.['pixel_anomaly.effective_status'];
  return (
    <Table.Cell>
      <StyledDropdown
        options={options}
        value={effectiveStatus}
        onChange={(e, { value }) => handleStatusChange(item, value)}
      />
    </Table.Cell>
  );
};

const TimeCell = ({ item, col }) => {
  const rawTime = item[col.key];
  const formattedDate = moment(rawTime).format('YYYY-MM-DD h:mmA');
  return (
    <Table.Cell>
      {formattedDate}
    </Table.Cell>
  );
};

const RatioCell = ({ item }) => (
  <Table.Cell>
    {' '}
    {item['pixel_anomaly.failed_checks_count']}
    /
    {item['pixel_anomaly.total_checks_count']}
  </Table.Cell>
);

const AnomalyDetectionTable = ({
  anomalyDetectionData,
  filter,
  setFilter,
  loading,
  advertiser,
  setAnomalyDetectionData,
}) => {
  const handleStatusChange = (item, updatedStatus) => {
    track(`data.event_status.dropdown.set_anomaly.${updatedStatus}`, { advertiser });

    const apiCall = item['pixel_anomaly.pref_status'] === null
      ? postPixelAnomalyNotificationPreference
      : putPixelAnomalyNotificationPreference;

    const data = item['pixel_anomaly.pref_status'] === null
      ? {
        action: item['pixel_anomaly.action'],
        anomaly_id: item['pixel_anomaly.id'],
        anomaly_type: item['pixel_anomaly.anomaly_type'],
        status: updatedStatus,
        param: item['pixel_anomaly.param'],
      }
      : {
        anomaly_id: item['pixel_anomaly.id'],
        status: updatedStatus,
      };

    apiCall(data)
      .then(() => {
        updateAnomalyItemStatus(item['pixel_anomaly.id'], updatedStatus, setAnomalyDetectionData);
      })
      .catch(error => {
        console.error('Error updating notification preference:', error);
      });
  };

  const cols = [
    {
      display: 'Action',
      key: 'pixel_anomaly.action',
    },
    {
      display: 'Type',
      key: 'pixel_anomaly.anomaly_type',
    },
    {
      display: 'Impacted Variable',
      key: 'pixel_anomaly.param',
      headerStyles: { whiteSpace: 'nowrap' },
    },
    {
      display: '% Change from Baseline',
      key: 'pixel_anomaly.metric_delta',
      as: PercentCell,
    },
    {
      display: 'Description',
      key: 'description',
      as: DescriptionCell,
    },
    {
      display: 'Failed Checks / Total Checks',
      key: 'pixel_anomaly.total_checks',
      as: RatioCell,
    },
    {
      display: 'First Seen (UTC)',
      key: 'pixel_anomaly.first_seen_exact',
      headerStyles: { whiteSpace: 'nowrap' },
      as: TimeCell,
    },
    {
      display: 'Last Seen (UTC)',
      key: 'pixel_anomaly.last_seen',
      headerStyles: { whiteSpace: 'nowrap' },
      as: TimeCell,
    },
    {
      display: 'Resolution Status',
      key: 'pixel_anomaly.effective_status',
      headerStyles: { whiteSpace: 'nowrap' },
      as: ResolutionStatusCell(handleStatusChange),
      sortable: false,
    },
  ];

  const handleFilterChange = filterValue => {
    track('data.event_status.dropdown.filter_anomaly', { advertiser });
    setFilter(filterValue);
  };

  return (
    loading && anomalyDetectionData.length === 0 ? <TreeTableLoader /> : (
      <div style={containerStyles}>
        <div style={bannerStyles}>
          <div>
            <h2 style={bannerHeader}>Event Violations and Anomalies</h2>
          </div>
          <div style={filterContainer}>
            <label>Filter by</label>
            <StyledDropdown
              options={options}
              value={filter}
              onChange={(e, { value }) => handleFilterChange(value)}
              containerStyles={{ paddingRight: 0 }}
            />
          </div>
        </div>
        <IndexGridNew
          data={anomalyDetectionData}
          cols={cols}
          sortable
          fallBackMsg="No Anomalies Detected"
          containerOverflowX="unset"
        />
      </div>
    ));
};

export default AnomalyDetectionTable;
